import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CaretUpIcon, CaretDownIcon, DownloadSimpleIcon, Spinner } from '@phosphor-icons/react';
import type { BucketedAlumni, ConstellationDetail } from '../../../services/dashboard.service';
import type { ConstellationDetails } from '../hooks/useConstellationDetails';
import { formatCompactINR } from '../../../utils/currencyFormatter';
import { useSessionState } from '../../../hooks/useSessionState';

/** One selectable group, in the order the sidebar lists it. */
export interface ConstellationBand {
  bucket: string;
  label: string;
  color: string;
}

/**
 * Sortable columns.
 *
 * No gap column: for the ~79% of alumni who have never given it prints the ask
 * again, and for the rest it is the subtraction of the two columns beside it.
 * The gap total in the footer is the part that is not derivable at a glance,
 * and it stays.
 *
 * Every one of these reads a number the client already holds for ALL rows, so
 * a sort is exact over the whole selection rather than over whatever happens to
 * be on screen. The descriptive columns (name, employer, batch) arrive one
 * window at a time from the details endpoint and are deliberately NOT sortable:
 * ordering 25k rows by name would mean ~500 round trips to learn the names
 * first.
 */
type SortKey = 'band' | 'ask' | 'giving' | 'opp';
type SortDir = 'asc' | 'desc';

/** Rows added per scroll step. Matches the details endpoint's per-call cap. */
const PAGE = 50;

/**
 * A worklist, not a data dump: the export walks the details endpoint 50 ids at
 * a time, so this is 20 round trips. Raising it is a one-constant change; a
 * genuine full-population export wants its own streaming endpoint instead.
 */
const EXPORT_CAP = 1000;

interface ConstellationTableProps {
  /** Already narrowed to the selected bands by the parent. */
  rows: BucketedAlumni[];
  bands: ConstellationBand[];
  details: ConstellationDetails;
  loading: boolean;
  /** Designation view: show VALS's current role instead of Salesforce's designation. */
  showRole?: boolean;
  /** A row was clicked: show that donor's profile. */
  onOpenDonor: (donorId: string) => void;
}

const gapOf = (r: BucketedAlumni) => (r.recommended_ask || 0) - (r.cumulative_giving || 0);

/**
 * The title and employer a row shows. The Designation view groups by VALS's
 * current role, so there it shows that role and company; every other view
 * keeps the Salesforce designation.
 */
const titleOf = (d: ConstellationDetail | undefined, showRole: boolean) =>
  (showRole ? d?.role : d?.designation) ?? '';
const companyOf = (d: ConstellationDetail | undefined, showRole: boolean) =>
  (showRole ? d?.role_company || d?.company : d?.company) ?? '';

/** RFC 4180: quote everything, double any embedded quote. */
const csvCell = (val: unknown): string => {
  if (val === null || val === undefined) return '""';
  return `"${String(val).replace(/"/g, '""')}"`;
};

export const ConstellationTable = ({ rows, bands, details, loading, showRole = false, onOpenDonor }: ConstellationTableProps) => {
  const { get, ensure, load } = details;

  // Session-backed so clicking through to a profile and back keeps the chosen order and window.
  const [sortKey, setSortKey] = useSessionState<SortKey>('constellation:sortKey', 'ask');
  const [sortDir, setSortDir] = useSessionState<SortDir>('constellation:sortDir', 'desc');
  const [pageWinN, setPageWinN] = useSessionState<number>('constellation:pageWinN', PAGE);
  // How far down the list is rendered, tagged with the list it belongs to.
  // Carrying the tag lets a new `sorted` invalidate the window during render
  // instead of in a second pass, so re-sorting never paints the old window
  // first and then snaps back to the top.
  const [pageWin, setPageWin] = useState<{ list: BucketedAlumni[]; n: number }>({
    list: [],
    n: pageWinN,
  });
  const [exportDone, setExportDone] = useState<number | null>(null);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const exporting = useRef(false);
  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
    },
    [],
  );

  const bandOf = useMemo(() => {
    const map = new Map<string, ConstellationBand & { order: number }>();
    bands.forEach((b, order) => map.set(b.bucket, { ...b, order }));
    return map;
  }, [bands]);

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const valueOf = (r: BucketedAlumni): number => {
      switch (sortKey) {
        case 'ask':
          return r.recommended_ask || 0;
        case 'giving':
          return r.cumulative_giving || 0;
        case 'opp':
          return r.opportunity_score || 0;
        case 'band':
          return bandOf.get(r.bucket)?.order ?? Number.MAX_SAFE_INTEGER;
      }
    };
    // donor_id breaks ties so equal rows keep a stable order instead of
    // reshuffling every time the selection changes.
    return [...rows].sort((a, b) => {
      const d = valueOf(a) - valueOf(b);
      return d !== 0 ? d * dir : a.donor_id.localeCompare(b.donor_id);
    });
  }, [rows, sortKey, sortDir, bandOf]);

  const visibleN = pageWin.list === sorted ? pageWin.n : pageWinN;

  const showMore = useCallback(() => {
    const nextN = Math.min(visibleN + PAGE, sorted.length);
    setPageWin({ list: sorted, n: nextN });
    setPageWinN(nextN);
  }, [sorted, visibleN, setPageWinN]);

  // Track scroll position of the table so opening a profile and pressing Back restores it
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      sessionStorage.setItem('constellation:tableScroll', String(el.scrollTop));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (el) sessionStorage.setItem('constellation:tableScroll', String(el.scrollTop));
    };
  }, []);

  // Restore scroll position once data is ready
  const restoredScrollRef = useRef(false);
  useEffect(() => {
    if (restoredScrollRef.current || !sorted.length || loading) return;
    const saved = Number(sessionStorage.getItem('constellation:tableScroll'));
    if (saved && scrollRef.current) {
      restoredScrollRef.current = true;
      requestAnimationFrame(() => {
        scrollRef.current?.scrollTo({ top: saved });
      });
    }
  }, [sorted, loading]);

  // When sorting changes explicitly, send the viewport back to the top
  const prevSortRef = useRef({ key: sortKey, dir: sortDir });
  useEffect(() => {
    if (prevSortRef.current.key !== sortKey || prevSortRef.current.dir !== sortDir) {
      prevSortRef.current = { key: sortKey, dir: sortDir };
      scrollRef.current?.scrollTo({ top: 0 });
      sessionStorage.setItem('constellation:tableScroll', '0');
    }
  }, [sortKey, sortDir]);

  // Names for the window on screen only. Cached ids are skipped inside the
  // hook, so re-sorting over rows already seen costs nothing.
  useEffect(() => {
    ensure(sorted.slice(0, visibleN).map((r) => r.donor_id));
  }, [sorted, visibleN, ensure]);

  useEffect(() => {
    const root = scrollRef.current;
    const target = sentinelRef.current;
    if (!root || !target || visibleN >= sorted.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) showMore();
      },
      { root, rootMargin: '160px' },
    );
    obs.observe(target);
    return () => obs.disconnect();
  }, [visibleN, sorted.length, showMore]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => {
          acc.ask += r.recommended_ask || 0;
          acc.gap += gapOf(r);
          return acc;
        },
        { ask: 0, gap: 0 },
      ),
    [rows],
  );

  const sortBy = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Money and opportunity read biggest-first; bands read in legend order.
      setSortDir(key === 'band' ? 'asc' : 'desc');
    }
  };

  const handleExport = useCallback(async () => {
    if (exporting.current || !sorted.length) return;
    exporting.current = true;
    const slice = sorted.slice(0, EXPORT_CAP);
    setExportDone(0);

    try {
      for (let i = 0; i < slice.length; i += PAGE) {
        const chunk = slice.slice(i, i + PAGE);
        await Promise.all(chunk.map((r) => load(r.donor_id)));
        if (!alive.current) return;
        setExportDone(i + chunk.length);
      }

      // Wider than the on-screen table: a CSV has no width budget, and every
      // field is already fetched by this point.
      const header = [
        'Name',
        'Batch',
        'Department',
        showRole ? 'Current Company (VALS)' : 'Company',
        showRole ? 'Current Role (VALS)' : 'Designation',
        'Location',
        'Band',
        'Target Ask (mid, INR)',
        'Cumulative Giving (INR)',
        'Gap (INR)',
        'Opportunity Score',
        'Confidence (%)',
        'Donor ID',
      ];
      const body = slice.map((r) => {
        const d = get(r.donor_id);
        return [
          d?.name ?? r.donor_id,
          d?.batch ?? '',
          d?.department ?? '',
          companyOf(d, showRole),
          titleOf(d, showRole),
          d?.work_location ?? '',
          bandOf.get(r.bucket)?.label ?? r.bucket,
          // Raw and unformatted: a spreadsheet has to be able to sum them.
          Math.round(r.recommended_ask || 0),
          Math.round(r.cumulative_giving || 0),
          Math.round(gapOf(r)),
          (r.opportunity_score || 0).toFixed(4),
          Math.round((d?.confidence ?? 0) * 100),
          r.donor_id,
        ]
          .map(csvCell)
          .join(',');
      });

      // BOM so Excel reads the file as UTF-8 rather than mangling names.
      const csv = ['﻿' + header.map(csvCell).join(','), ...body].join('\r\n');
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `alumni-constellation-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV export failed', err);
    } finally {
      exporting.current = false;
      if (alive.current) setExportDone(null);
    }
  }, [sorted, load, get, bandOf, showRole]);

  const columns: Array<{ key: SortKey | null; label: string; className: string }> = [
    { key: 'band', label: '', className: 'w-[18px] pl-2.5' },
    { key: null, label: 'Alumni', className: 'pl-2' },
    { key: null, label: 'Batch', className: 'w-[44px] text-right' },
    { key: 'ask', label: 'Ask', className: 'w-[76px] text-right' },
    { key: 'giving', label: 'Given', className: 'w-[76px] text-right' },
    { key: 'opp', label: 'Opp', className: 'w-[48px] text-right pr-3' },
  ];

  const isExporting = exportDone !== null;
  const exportTotal = Math.min(sorted.length, EXPORT_CAP);

  return (
    <div className="flex-1 min-w-0 flex flex-col border-l border-slate-100 bg-white">
      {/* List header */}
      <div className="px-3 py-2.5 flex items-center justify-between gap-2 border-b border-slate-100 shrink-0">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 truncate">
          Selected Alumni
          <span className="ml-1.5 font-medium normal-case tracking-normal text-slate-300">
            {loading ? '…' : rows.length.toLocaleString('en-IN')}
          </span>
        </span>
        <button
          onClick={handleExport}
          disabled={isExporting || !rows.length}
          title={
            sorted.length > EXPORT_CAP
              ? `Downloads the top ${EXPORT_CAP.toLocaleString('en-IN')} rows of the current sort`
              : 'Download the current selection as CSV'
          }
          className="flex items-center gap-1 text-[10px] font-semibold text-accent hover:underline disabled:text-slate-300 disabled:no-underline disabled:cursor-not-allowed shrink-0"
        >
          {isExporting ? (
            <>
              <Spinner size={11} className="animate-spin" />
              {exportDone}/{exportTotal}
            </>
          ) : (
            <>
              <DownloadSimpleIcon size={11} weight="bold" />
              CSV
            </>
          )}
        </button>
      </div>

      {/* Rows */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-auto">
        <table className="w-full min-w-[360px] table-fixed border-collapse text-left">
          <thead className="sticky top-0 z-10 bg-white">
            <tr className="border-b border-slate-100 text-slate-400 text-[9px] font-bold uppercase tracking-wider">
              {columns.map(({ key, label, className }, i) => (
                <th
                  key={label || `col-${i}`}
                  scope="col"
                  className={`${className} py-2 font-bold ${
                    key ? 'cursor-pointer hover:text-slate-700 transition-colors select-none' : ''
                  }`}
                  onClick={key ? () => sortBy(key) : undefined}
                  title={
                    key
                      ? `Sort by ${label || 'group'}`
                      : 'Fetched one screenful at a time — not sortable'
                  }
                >
                  <div
                    className={`flex items-center gap-0.5 ${
                      className.includes('text-right') ? 'justify-end' : ''
                    }`}
                  >
                    {label}
                    {key && sortKey === key ? (
                      sortDir === 'asc' ? (
                        <CaretUpIcon size={9} weight="bold" className="shrink-0" />
                      ) : (
                        <CaretDownIcon size={9} weight="bold" className="shrink-0" />
                      )
                    ) : null}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={`sk-${i}`}>
                  <td colSpan={columns.length} className="px-3 py-[9px]">
                    <div className="h-3 rounded bg-slate-100 animate-pulse" />
                  </td>
                </tr>
              ))
            ) : !sorted.length ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="py-10 text-center text-[11px] text-slate-400 font-medium"
                >
                  {bands.length > 0
                    ? 'No groups selected — pick one from the panel.'
                    : 'No alumni with a target ask match these filters.'}
                </td>
              </tr>
            ) : (
              sorted.slice(0, visibleN).map((r) => {
                const band = bandOf.get(r.bucket);
                const detail = get(r.donor_id);
                return (
                  <tr
                    key={r.donor_id}
                    onClick={() => onOpenDonor(r.donor_id)}
                    className="hover:bg-slate-50/80 transition-colors cursor-pointer group"
                    title={
                      detail
                        ? `${detail.name} — confidence ${Math.round((detail.confidence ?? 0) * 100)}%`
                        : r.donor_id
                    }
                  >
                    <td className="pl-2.5 py-[7px] align-top">
                      <span
                        className="block w-2 h-2 rounded-full mt-[3px]"
                        style={{ backgroundColor: band?.color ?? '#94a3b8' }}
                        title={band?.label ?? r.bucket}
                      />
                    </td>
                    <td className="pl-2 py-[7px] min-w-0">
                      {detail ? (
                        <>
                          <div className="truncate text-[11px] font-bold text-slate-800 group-hover:text-accent transition-colors">
                            {detail.name}
                          </div>
                          <div className="truncate text-[9.5px] text-slate-400 font-medium">
                            {[titleOf(detail, showRole), companyOf(detail, showRole)].filter(Boolean).join(' @ ') || '—'}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="h-[11px] w-3/4 rounded bg-slate-100 animate-pulse" />
                          <div className="h-[9px] w-1/2 rounded bg-slate-50 animate-pulse mt-1" />
                        </>
                      )}
                    </td>
                    <td className="py-[7px] text-right text-[10px] tabular-nums font-semibold text-slate-600 align-top">
                      {detail?.batch ?? ''}
                    </td>
                    <td className="py-[7px] text-right text-[10px] tabular-nums font-bold text-emerald-600 align-top">
                      {formatCompactINR(r.recommended_ask)}
                    </td>
                    <td className="py-[7px] text-right text-[10px] tabular-nums font-semibold text-slate-700 align-top">
                      {formatCompactINR(r.cumulative_giving)}
                    </td>
                    <td className="py-[7px] pr-3 text-right text-[10px] tabular-nums font-bold text-accent align-top">
                      {(r.opportunity_score || 0).toFixed(2)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Scroll sentinel, with an explicit button so the list still grows if
            the observer never fires. */}
        {!loading && visibleN < sorted.length && (
          <div ref={sentinelRef} className="py-2 text-center">
            <button
              onClick={showMore}
              className="text-[10px] font-semibold text-slate-400 hover:text-accent transition-colors"
            >
              Show {Math.min(PAGE, sorted.length - visibleN)} more
              <span className="text-slate-300 font-medium">
                {' '}
                · {visibleN.toLocaleString('en-IN')} of {sorted.length.toLocaleString('en-IN')}
              </span>
            </button>
          </div>
        )}
      </div>

      {/* Totals for the whole selection, not just the rows rendered. */}
      <div className="px-3 py-2 border-t border-slate-100 bg-slate-50/60 shrink-0 flex items-center justify-between gap-2 text-[10px] tabular-nums">
        <span className="font-semibold text-slate-500 truncate">
          {rows.length.toLocaleString('en-IN')} selected
        </span>
        <span className="flex items-center gap-3 shrink-0">
          <span className="text-slate-400 font-medium">
            Ask <span className="font-bold text-emerald-600">{formatCompactINR(totals.ask)}</span>
          </span>
          <span className="text-slate-400 font-medium">
            Gap{' '}
            {/* Giving can exceed the ask, which makes a gap negative — already
                given more than we mean to ask for. Rose is for headroom left
                to raise, not for a number that happens to be large. */}
            <span className={`font-bold ${totals.gap > 0 ? 'text-rose-500' : 'text-emerald-600'}`}>
              {formatCompactINR(totals.gap)}
            </span>
          </span>
        </span>
      </div>
    </div>
  );
};
